import React from 'react';
import Icon from '../../../components/AppIcon';

const ValuesSection = () => {
  const coreValues = [
    {
      icon: "Users",
      title: "Collaborative Excellence",
      description: "Great software is built by great teams. I thrive in environments where knowledge sharing, peer reviews, and collective problem-solving drive innovation.",
      practices: [
        "Demonstrated strong verbal and written communication skills in a professional environment.",
        "Successfully collaborated in a remote setup on hands-on projects.",
        "Utilized leadership and team management abilities to drive project completion.",
        "Practiced effective time management and organizational skills to meet deadlines."
      ],
      color: "trust-green"
    },
    {
      icon: "Lightbulb",
      title: "Innovation Mindset",
      description: "Constantly exploring new technologies and methodologies to solve problems more efficiently and create better user experiences.",
      practices: [
        "Experimenting with emerging frameworks",
        "Proposing process improvements",
        "Building proof-of-concept solutions",
        "Attending tech conferences and workshops"
      ],
      color: "electric-cyan"
    },
    {
      icon: "Award",
      title: "Quality Commitment",
      description: "Delivering code that not only works but is maintainable, scalable, and follows industry best practices and standards.",
      practices: [
        "Test-driven development approach",
        "Comprehensive documentation",
        "Performance optimization focus",
        "Security-first implementation"
      ],
      color: "rocket-fire"
    }
  ];

  const communityInvolvement = [
    {
      activity: "Open Source Contributions",
      description: "Active contributor to React ecosystem projects",
      impact: "500+ GitHub stars across repositories",
      icon: "Github"
    },
    {
      activity: "Tech Community Speaker",
      description: "Presented at university tech talks and local meetups",
      impact: "Reached 200+ aspiring developers",
      icon: "Mic"
    },
    {
      activity: "Coding Bootcamp Mentor",
      description: "Volunteer mentor for career-change developers",
      impact: "Guided 15+ students to successful careers",
      icon: "GraduationCap"
    },
    {
      activity: "Hackathon Organizer",
      description: "Co-organized university\'s annual hackathon",
      impact: "300+ participants, $10K in prizes",
      icon: "Trophy"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background bg-gradient-to-br from-deep-space via-slate-900 to-slate-800">
      <div id="CoreValues" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Heart" size={24} className="text-rocket-fire" />
            <span className="text-rocket-fire font-medium">Core Values</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline text-deep-space mb-4 text-white">
            What Drives Me Forward
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            My professional values shape every line of code I write and every team I join. These principles guide my approach to software development and collaboration.
          </p>
        </div>

        {/* Core Values */}
        <div className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {coreValues?.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-brand hover:shadow-brand-lg transition-all duration-300">
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 bg-${value?.color}/10 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Icon name={value?.icon} size={28} className={`text-${value?.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-deep-space">{value?.title}</h3>
                </div>
                
                <p className="text-text-secondary mb-6 text-center">{value?.description}</p>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-deep-space uppercase tracking-wide">In Practice:</h4>
                  {value?.practices?.map((practice, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className={`text-${value?.color} mt-1 flex-shrink-0`} />
                      <span className="text-sm text-text-primary">{practice}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;